import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridOptions, GridEmitter, UiGridComponent } from '@program-suite/ui-grid';
import { ButtonComponent } from '@program-suite/ui';
import { AdminSettingsStore } from '../../data/admin-settings.store';
import { UserViewModel } from '@program-suite/data-common';
import { ModalService, ModalType } from '@program-suite/ui';
import { AddAdminComponent } from '../../modals/add-admin/add-admin.component';
import { SnackbarComponent, ToastType } from '@program-suite/ui';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmModalComponent, ConfirmModalData } from '@program-suite/ui';
import { AuthStore } from '@program-suite/data-auth';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'lib-admin-users',
  imports: [CommonModule, UiGridComponent, ButtonComponent],
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss', '../admin-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUsersComponent {
  authStore = inject(AuthStore);
  pageSizeMax = 2147483647;
  filterValues = signal<any>({ pageSize: this.pageSizeMax });
  data = computed<UserViewModel[]>(() =>
    this.adminSettingsStore.wholesalers ? this.adminSettingsStore.wholesalers() ?? [] : [],
  );
  adminSettingsStore = inject(AdminSettingsStore);
  gridOptions = signal<GridOptions>({
    filterData: [],
    filterValues: this.filterValues,
    openFilters: true,
    columns: [
      { header: 'First Name', controlName: 'firstName' },
      { header: 'Last Name', controlName: 'lastName' },
      { header: 'Email', controlName: 'email' },
      { header: 'Role', controlName: 'roles' },
      { header: 'Status', controlName: 'status' },
      { header: 'Created Date', controlName: 'createDate', displayPipe: 'date' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'icon',
            buttonAction: 'editUser',
            conditionalDisplay: {
              calculateDisplay: (status) => {
                return 'eye';
              },
              column: 'status',
            },
            hideOnColumnValue: { column: 'editUser', values: [false] },
          },
          {
            type: 'dropdown',
            buttonAction: 'action',
            hideOnColumnValue: { column: 'editUser', values: [true] },
            conditionalDisplay: { calculateDisplay: () => 'ellipsis', column: 'status' },
            dropdownItems: computed(() => {
              const items = [
                {
                  name: 'Resend Invite',
                  buttonAction: 'resendInvite',
                  conditionalDisabled: { calculateDisabled: (bool: boolean) => bool == false, column: 'resendInvite' },
                },
                {
                  name: 'Cancel Invite',
                  buttonAction: 'cancelInvite',
                  conditionalDisabled: { calculateDisabled: (bool: boolean) => bool == false, column: 'cancelInvite' },
                },
              ];
              return items;
            }),
          },
        ],
      },
    ],
    data: this.data,
    frontEndPagination: true,
    hidepagination: true,
  });

  constructor(private modalService: ModalService, private snackBar: MatSnackBar, private route: ActivatedRoute) {}

  fetchAll(data: any) {
    if (this.authStore.isAdmin()) {
      const tenantId = this.route.snapshot.queryParams['tenantId'];
      this.adminSettingsStore.getWholesalers(tenantId)
    } else {
      this.adminSettingsStore.getWholesalers();
    }
  }

  resendWholesalerInvite(data: UserViewModel) {
    this.adminSettingsStore
      .resendInvite(data.userInviteGuid ?? '')
      .then(() => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Success, message: 'Resent Invite' },
        });
      })
      .catch((error) => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: error.message },
        });
      });
  }

  addWholesaler() {
    this.modalService.open(AddAdminComponent, {
      modalType: ModalType.FullScreen,
      data: {
        title: 'Add Admin User',
        subTitle: null,
        pTitle: 'Enter the following details for your admin user.',
        pClosing: 'An invitation email will be sent to with instructions to complete their registration.',
        button: 'Add Admin User',
      },
    });
  }

  editWholesaler(data: UserViewModel) {
    this.modalService.open(AddAdminComponent, {
      modalType: ModalType.FullScreen,
      data: {
        title: 'Update User',
        subTitle: data.roles![0],
        pTitle: 'Enter the following details to update your user.',
        button: 'Update User',
        user: data,
      },
    });
  }

  cancelInvite(data: UserViewModel) {
    const confirmData: ConfirmModalData = {
      confirmString: 'CANCEL',
      title: 'Are you sure?',
      subtitle: '',
      message: `Cancelling this invitation will deactivate the registration links in 
                previously sent to ${data.firstName} ${data.lastName} and remove the 
                history from your agency users grid.`,
      confirmButtonText: 'Cancel Invite',
    };
    this.modalService.open(
      ConfirmModalComponent,
      {
        modalType: ModalType.Small,
        data: confirmData,
      },
      (response: any) => {
        if (!response) {
          return;
        }
        this.adminSettingsStore
          .cancelInvite(data.userInviteGuid ?? '')
          .then(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Success, message: 'Invite Cancelled' },
            });
          })
          .catch((error) => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Error, message: error.message },
            });
          });
      },
    );
  }

  actionEvent(event: GridEmitter) {
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      // form changes
    } else if (event.action === 'clearFilters') {
      // clear filters
    } else if (event.action === 'onDestroy') {
      // On destroy
    } else if (event.action === 'editUser') {
      const data = event.data as UserViewModel;
      this.editWholesaler(data);
    } else if (event.action === 'resendInvite') {
      const data = event.data as UserViewModel;
      this.resendWholesalerInvite(data);
    } else if (event.action === 'cancelInvite') {
      const data = event.data as UserViewModel;
      this.cancelInvite(data);
    }
  }
}
